/**
 * Typed API client.
 *
 * - Uses `credentials: 'include'` so the session cookie is sent on every request.
 * - For unsafe methods (POST/PUT/DELETE), automatically adds `X-CSRF-Token`
 *   from the in-memory token cache. The token is set once on bootstrap
 *   (from /api/auth/me or /api/auth/login) and refreshed on demand.
 * - Throws `ApiError` on non-2xx responses, with the backend's code/message
 *   when available.
 * - Parses every successful response through a runtime zod schema so backend
 *   drift surfaces as `ApiError(code: 'INVALID_RESPONSE')` at the call site
 *   instead of a crash deep inside a renderer.
 */

import type { ZodType } from 'zod';
import {
  AuthMeResponseSchema,
  MapRecordSchema,
  MapSummaryListSchema,
  MapWithTokensSchema,
  TelemetryConfigSchema,
  TokenListSchema,
  TokenSchema,
} from './schemas.js';
import { currentTraceparentHeader, withSpan } from './telemetry.js';
import {
  ApiError,
  type EncounterState,
  type ExploredCells,
  type MapOverlay,
  type MapRecord,
  type MapSummary,
  type Token,
  type User,
  type Viewport,
} from './types.js';

let csrfToken: string | null = null;

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  jsonBody?: unknown;
  formBody?: FormData;
  /**
   * Runtime schema applied to the parsed response. Required for non-void
   * endpoints; omit only for endpoints whose body we discard (204s or
   * `{ success: true }` ack payloads).
   */
  schema?: ZodType<unknown>;
}

const UNSAFE_METHODS = new Set(['POST', 'PUT', 'DELETE']);

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const method = opts.method ?? 'GET';
  return withSpan(`HTTP ${method}`, async (span) => {
    span.setAttribute('http.method', method);
    span.setAttribute('http.url', path);
    return doFetch<T>(method, path, opts, span);
  });
}

async function doFetch<T>(
  method: string,
  path: string,
  opts: RequestOptions,
  span: import('@opentelemetry/api').Span,
): Promise<T> {
  const headers: Record<string, string> = {};
  let body: BodyInit | undefined;

  if (opts.jsonBody !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.jsonBody);
  } else if (opts.formBody !== undefined) {
    // Let the browser set the multipart boundary itself — don't override Content-Type.
    body = opts.formBody;
  }

  if (UNSAFE_METHODS.has(method) && csrfToken !== null) {
    headers['X-CSRF-Token'] = csrfToken;
  }

  // Inject W3C traceparent so the backend can continue the trace. The browser
  // span we're inside becomes the parent of the PHP server span.
  // Skipped for the telemetry export endpoint itself to avoid feedback loops.
  if (path !== '/api/telemetry/v1/traces') {
    const tp = currentTraceparentHeader();
    if (tp !== null) {
      headers['traceparent'] = tp;
    }
  }

  const init: RequestInit = {
    method,
    credentials: 'include',
    headers,
  };
  if (body !== undefined) {
    init.body = body;
  }

  const response = await fetch(path, init);
  span.setAttribute('http.status_code', response.status);

  if (!response.ok) {
    let code = 'HTTP_ERROR';
    let message = `HTTP ${response.status}`;
    try {
      const data = (await response.json()) as { error?: { code?: string; message?: string } };
      if (data?.error?.code) code = data.error.code;
      if (data?.error?.message) message = data.error.message;
    } catch {
      // Body wasn't JSON — fall back to defaults.
    }
    throw new ApiError(response.status, code, message);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  const raw: unknown = await response.json();
  if (opts.schema === undefined) {
    return raw as T;
  }
  const parsed = opts.schema.safeParse(raw);
  if (!parsed.success) {
    // Log the structured zod issues for the developer; show a stable
    // user-facing message via ApiError so call sites can handle uniformly.
    console.error(`[api] invalid response from ${method} ${path}`, parsed.error.issues);
    throw new ApiError(
      response.status,
      'INVALID_RESPONSE',
      `Unexpected response shape from ${path}`,
    );
  }
  return parsed.data as T;
}

// --- Auth ---------------------------------------------------------------------

export interface AuthMeResponse {
  user: User;
  csrf_token: string;
}

export async function getMe(): Promise<AuthMeResponse> {
  const res = await request<AuthMeResponse>('/api/auth/me', { schema: AuthMeResponseSchema });
  setCsrfToken(res.csrf_token);
  return res;
}

export async function login(email: string, password: string): Promise<AuthMeResponse> {
  const res = await request<AuthMeResponse>('/api/auth/login', {
    method: 'POST',
    jsonBody: { email, password },
    schema: AuthMeResponseSchema,
  });
  setCsrfToken(res.csrf_token);
  return res;
}

export async function logout(): Promise<void> {
  await request<{ success: true }>('/api/auth/logout', { method: 'POST' });
  setCsrfToken(null);
}

// --- Maps ---------------------------------------------------------------------

export function getMaps(): Promise<MapSummary[]> {
  return request<MapSummary[]>('/api/maps', { schema: MapSummaryListSchema });
}

export function getMap(id: number): Promise<{ map: MapRecord; tokens: Token[] }> {
  return request<{ map: MapRecord; tokens: Token[] }>(`/api/maps/${id}`, {
    schema: MapWithTokensSchema,
  });
}

export function createMap(form: FormData): Promise<MapRecord> {
  return request<MapRecord>('/api/maps', {
    method: 'POST',
    formBody: form,
    schema: MapRecordSchema,
  });
}

/** Partial-update a map's metadata. Currently only `name` is supported. */
export function renameMap(id: number, name: string): Promise<MapRecord> {
  return request<MapRecord>(`/api/maps/${id}`, {
    method: 'PUT',
    jsonBody: { name },
    schema: MapRecordSchema,
  });
}

export function updateViewport(id: number, viewport: Viewport): Promise<void> {
  return request<void>(`/api/maps/${id}/viewport`, {
    method: 'PUT',
    jsonBody: { viewport_x: viewport.x, viewport_y: viewport.y, zoom: viewport.zoom },
  });
}

export function deleteMap(id: number): Promise<void> {
  return request<void>(`/api/maps/${id}`, { method: 'DELETE' });
}

/** Replace the wall/door overlay for a map (full PUT). */
export function updateOverlay(id: number, overlay: MapOverlay): Promise<void> {
  return request<void>(`/api/maps/${id}/overlay`, { method: 'PUT', jsonBody: overlay });
}

/** Replace the explored-cells set for a map (full PUT). Empty object clears it. */
export function updateExplored(id: number, explored: ExploredCells): Promise<void> {
  return request<void>(`/api/maps/${id}/explored`, { method: 'PUT', jsonBody: explored });
}

/** Replace the initiative encounter for a map. `null` clears it. */
export function updateEncounter(id: number, encounter: EncounterState | null): Promise<void> {
  return request<void>(`/api/maps/${id}/encounter`, {
    method: 'PUT',
    jsonBody: encounter ?? {},
  });
}

// --- Tokens -------------------------------------------------------------------

/**
 * Create one or more tokens in a single request. The backend always returns
 * an array — length 1 for a single-create, length N when `count > 1` was sent.
 * Use `const [token] = await createTokens(...)` for the single-create case.
 */
export function createTokens(mapId: number, form: FormData): Promise<Token[]> {
  return request<Token[]>(`/api/maps/${mapId}/tokens`, {
    method: 'POST',
    formBody: form,
    schema: TokenListSchema,
  });
}

export function updateToken(
  id: number,
  patch: Partial<Pick<Token, 'grid_x' | 'grid_y' | 'status_text' | 'is_downed' | 'is_placed'>>,
): Promise<Token> {
  return request<Token>(`/api/tokens/${id}`, {
    method: 'PUT',
    jsonBody: patch,
    schema: TokenSchema,
  });
}

export function deleteToken(id: number): Promise<void> {
  return request<void>(`/api/tokens/${id}`, { method: 'DELETE' });
}

// --- Telemetry ----------------------------------------------------------------

export interface TelemetryConfigResponse {
  enabled: boolean;
  serviceName: string;
  sampleRate: number;
}

export function getTelemetryConfig(): Promise<TelemetryConfigResponse> {
  return request<TelemetryConfigResponse>('/api/telemetry/config', {
    schema: TelemetryConfigSchema,
  });
}
